import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import api from "@/lib/api";
import { Search, Plus, User, MapPin, Heart, Users } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const depColors = {
  autovalido: { bg: "bg-emerald-50", text: "text-emerald-700", label: "Autoválido" },
  dependencia_media: { bg: "bg-amber-50", text: "text-amber-700", label: "Dep. Media" },
  dependencia_total: { bg: "bg-rose-50", text: "text-rose-600", label: "Dep. Total" },
};

export default function ResidentsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canCreate = user?.role === "super_admin" || user?.role === "tenant_admin" || user?.permissions?.includes("crear_pacientes");
  const [residents, setResidents] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState({
    name: "", dni: "", obra_social: "PAMI", room_number: "", dependency_level: "autovalido",
    fecha_nacimiento: "", fecha_ingreso: new Date().toISOString().split("T")[0],
    numero_afiliado: "", emergency_contact_name: "", emergency_contact_phone: ""
  });
  const [doctorName, setDoctorName] = useState("");
  const [doctors, setDoctors] = useState([]);
  const [addFamily, setAddFamily] = useState(false);
  const [famForm, setFamForm] = useState({ name: "", email: "", phone: "", relationship: "hijo" });
  const [newCredentials, setNewCredentials] = useState(null);

  const fetchResidents = useCallback(async () => {
    try {
      const r = await api.get("/residents", { params: search ? { search } : {} });
      setResidents(r.data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [search]);

  useEffect(() => { fetchResidents(); }, [fetchResidents]);

  useEffect(() => {
    if (canCreate) {
      api.get("/doctors").then(r => setDoctors(r.data)).catch(() => {});
    }
  }, [canCreate]);

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      const residentRes = await api.post("/residents", form);
      const residentId = residentRes.data.id;

      // Link or create doctor
      if (doctorName.trim()) {
        try {
          const existing = doctors.find(d => d.name.toLowerCase() === doctorName.trim().toLowerCase());
          let doctorId;
          if (existing) {
            doctorId = existing.id;
          } else {
            const docRes = await api.post("/doctors", { name: doctorName.trim() });
            doctorId = docRes.data.id;
          }
          await api.post(`/residents/${residentId}/doctors`, { doctor_id: doctorId, role: "cabecera" });
        } catch { /* non-blocking */ }
      }

      let famResult = null;
      if (addFamily && famForm.name && famForm.email) {
        try {
          const r = await api.post(`/residents/${residentId}/family`, famForm);
          if (r.data.is_new) {
            famResult = { email: r.data.email, password: r.data.temp_password };
          }
        } catch { /* non-blocking */ }
      }
      if (famResult) {
        setNewCredentials(famResult);
        toast.success("Residente y familiar creados");
      } else {
        toast.success("Residente creado correctamente");
        setDialogOpen(false);
      }
      setForm({
        name: "", dni: "", obra_social: "PAMI", room_number: "", dependency_level: "autovalido",
        fecha_nacimiento: "", fecha_ingreso: new Date().toISOString().split("T")[0],
        numero_afiliado: "", emergency_contact_name: "", emergency_contact_phone: ""
      });
      setDoctorName("");
      setFamForm({ name: "", email: "", phone: "", relationship: "hijo" });
      setAddFamily(false);
      fetchResidents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al crear residente");
    }
  };

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="residents-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight font-[Manrope]">Residentes</h1>
          <p className="text-sm text-slate-400 mt-0.5">{residents.length} registrados</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          {canCreate && (
            <DialogTrigger asChild>
              <Button className="rounded-xl px-5 h-10 bg-violet-600 text-white hover:bg-violet-700 active:scale-[0.98] transition-all duration-200 text-sm font-semibold" data-testid="add-resident-btn">
                <Plus className="w-4 h-4 mr-2" /> Nuevo Residente
              </Button>
            </DialogTrigger>
          )}
          <DialogContent className="rounded-2xl sm:max-w-md max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-slate-900 font-bold font-[Manrope]">Nuevo Residente</DialogTitle>
              <DialogDescription className="text-slate-400 text-sm">Complete los datos del nuevo residente</DialogDescription>
            </DialogHeader>
            {newCredentials ? (
              <div className="space-y-4 mt-2">
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4">
                  <p className="text-sm font-semibold text-emerald-700 mb-1">Residente y familiar creados</p>
                  <p className="text-sm text-emerald-600">Credenciales del familiar:</p>
                </div>
                <div className="bg-slate-50 rounded-xl p-4 space-y-2">
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase tracking-widest font-semibold">Email</p>
                    <p className="text-sm font-mono font-semibold text-slate-900">{newCredentials.email}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 uppercase tracking-widest font-semibold">Contraseña temporal</p>
                    <p className="text-sm font-mono font-semibold text-slate-900">{newCredentials.password}</p>
                  </div>
                </div>
                <Button onClick={() => { setDialogOpen(false); setNewCredentials(null); }} className="w-full rounded-xl h-10 bg-violet-600 text-white font-semibold hover:bg-violet-700">
                  Entendido
                </Button>
              </div>
            ) : (
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium text-sm">Nombre Completo</Label>
                <Input value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="rounded-xl h-10 bg-slate-50 border-slate-200 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/10" required data-testid="resident-name-input" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-slate-700 font-medium text-sm">DNI</Label>
                  <Input value={form.dni} onChange={e => setForm({...form, dni: e.target.value})} className="rounded-xl h-10 bg-slate-50 border-slate-200 focus:border-violet-500" required data-testid="resident-dni-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-700 font-medium text-sm">Habitación</Label>
                  <Input value={form.room_number} onChange={e => setForm({...form, room_number: e.target.value})} className="rounded-xl h-10 bg-slate-50 border-slate-200 focus:border-violet-500" data-testid="resident-room-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-slate-700 font-medium text-sm">Fecha de Nacimiento</Label>
                  <Input type="date" value={form.fecha_nacimiento} onChange={e => setForm({...form, fecha_nacimiento: e.target.value})} className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="resident-birth-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-700 font-medium text-sm">Fecha de Ingreso</Label>
                  <Input type="date" value={form.fecha_ingreso} onChange={e => setForm({...form, fecha_ingreso: e.target.value})} className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="resident-admission-input" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label className="text-slate-700 font-medium text-sm">Obra Social</Label>
                  <Select value={form.obra_social} onValueChange={v => setForm({...form, obra_social: v})}>
                    <SelectTrigger className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="resident-obra-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="PAMI">PAMI</SelectItem>
                      <SelectItem value="IOMA">IOMA</SelectItem>
                      <SelectItem value="OSDE">OSDE</SelectItem>
                      <SelectItem value="Swiss Medical">Swiss Medical</SelectItem>
                      <SelectItem value="Otra">Otra</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-700 font-medium text-sm">N° Afiliado</Label>
                  <Input value={form.numero_afiliado} onChange={e => setForm({...form, numero_afiliado: e.target.value})} placeholder="Opcional" className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="resident-affiliate-input" />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 font-medium text-sm">Dependencia</Label>
                <Select value={form.dependency_level} onValueChange={v => setForm({...form, dependency_level: v})}>
                  <SelectTrigger className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="resident-dep-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="autovalido">Autoválido</SelectItem>
                    <SelectItem value="dependencia_media">Dependencia Media</SelectItem>
                    <SelectItem value="dependencia_total">Dependencia Total</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Médico de cabecera */}
              <div className="border-t border-slate-100 pt-4">
                <Label className="text-slate-700 font-medium text-sm">Médico de Cabecera</Label>
                <Input
                  value={doctorName}
                  onChange={e => setDoctorName(e.target.value)}
                  placeholder="Nombre y apellido del médico"
                  className="rounded-xl h-10 bg-slate-50 border-slate-200 mt-1.5"
                  list="doctors-list"
                  data-testid="resident-doctor-input"
                />
                <datalist id="doctors-list">
                  {doctors.map(d => <option key={d.id} value={d.name} />)}
                </datalist>
                <p className="text-[10px] text-slate-400 mt-1">Si no existe se creará automáticamente. Podés completar matrícula y especialidad después en Médicos.</p>
              </div>

              {/* Contacto de emergencia */}
              <div className="border-t border-slate-100 pt-4">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Contacto de Emergencia</p>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium text-sm">Nombre</Label>
                    <Input value={form.emergency_contact_name} onChange={e => setForm({...form, emergency_contact_name: e.target.value})} placeholder="Familiar / responsable" className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="resident-emergency-name" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-slate-700 font-medium text-sm">Teléfono</Label>
                    <Input value={form.emergency_contact_phone} onChange={e => setForm({...form, emergency_contact_phone: e.target.value})} placeholder="011 ..." className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="resident-emergency-phone" />
                  </div>
                </div>
              </div>

              {/* Optional Family Member */}
              <div className="border-t border-slate-100 pt-4">
                <label className="flex items-center gap-2 cursor-pointer" data-testid="add-family-checkbox">
                  <Checkbox checked={addFamily} onCheckedChange={setAddFamily} className="data-[state=checked]:bg-violet-600" />
                  <span className="text-sm font-medium text-slate-700">Agregar un familiar</span>
                </label>
                {addFamily && (
                  <div className="space-y-3 mt-3 animate-fade-in-up">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label className="text-slate-700 font-medium text-sm">Nombre familiar</Label>
                        <Input value={famForm.name} onChange={e => setFamForm({ ...famForm, name: e.target.value })} className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="res-fam-name" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-700 font-medium text-sm">Parentesco</Label>
                        <Select value={famForm.relationship} onValueChange={v => setFamForm({ ...famForm, relationship: v })}>
                          <SelectTrigger className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="res-fam-rel"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="hijo">Hijo/a</SelectItem>
                            <SelectItem value="esposo">Esposo/a</SelectItem>
                            <SelectItem value="nieto">Nieto/a</SelectItem>
                            <SelectItem value="hermano">Hermano/a</SelectItem>
                            <SelectItem value="otro">Otro</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div className="space-y-2">
                        <Label className="text-slate-700 font-medium text-sm">Email</Label>
                        <Input type="email" value={famForm.email} onChange={e => setFamForm({ ...famForm, email: e.target.value })} className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="res-fam-email" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-slate-700 font-medium text-sm">Teléfono</Label>
                        <Input value={famForm.phone} onChange={e => setFamForm({ ...famForm, phone: e.target.value })} placeholder="Opcional" className="rounded-xl h-10 bg-slate-50 border-slate-200" data-testid="res-fam-phone" />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <Button type="submit" className="w-full rounded-xl h-10 bg-violet-600 text-white font-semibold hover:bg-violet-700 active:scale-[0.98] transition-all" data-testid="submit-resident-btn">
                Crear Residente
              </Button>
            </form>
            )}
          </DialogContent>
        </Dialog>
      </div>

      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Buscar por nombre, DNI o habitación..."
          className="rounded-xl h-11 pl-11 bg-white border-slate-200 shadow-sm text-slate-900 focus:border-violet-500 focus:ring-2 focus:ring-violet-500/10"
          data-testid="search-residents-input"
        />
      </div>

      {loading ? (
        <p className="text-slate-400 text-center py-12">Cargando residentes...</p>
      ) : residents.length === 0 ? (
        <div className="text-center py-16">
          <User className="w-10 h-10 text-slate-300 mx-auto mb-3" strokeWidth={1.5} />
          <p className="text-slate-400 font-medium">No se encontraron residentes</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {residents.map((r) => {
            const dep = depColors[r.dependency_level] || depColors.autovalido;
            return (
              <Card
                key={r.id}
                className="rounded-2xl border-slate-100 shadow-sm cursor-pointer hover:shadow-md transition-all duration-200 hover:-translate-y-[1px]"
                onClick={() => navigate(`/residents/${r.id}`)}
                data-testid={`resident-card-${r.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    <div className="w-11 h-11 rounded-xl bg-violet-50 flex items-center justify-center text-violet-600 font-semibold text-base flex-shrink-0">
                      {r.name?.charAt(0)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-900 truncate text-sm">{r.name}</h3>
                      <p className="text-xs text-slate-400 mt-0.5">DNI: {r.dni}</p>
                      <div className="flex items-center gap-2 mt-2 flex-wrap">
                        <Badge className={`rounded-full px-2.5 py-0.5 text-[10px] font-semibold ${dep.bg} ${dep.text} border-0`}>
                          {dep.label}
                        </Badge>
                        {r.room_number && (
                          <span className="text-[11px] text-slate-400 flex items-center gap-1">
                            <MapPin className="w-3 h-3" /> Hab. {r.room_number}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-1 mt-1.5">
                        <Heart className="w-3 h-3 text-slate-300" />
                        <span className="text-[11px] text-slate-400">{r.obra_social}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
